import styled from "styled-components";

const LandingPageStyled = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colour.brand.primary} 0%,
    ${(props) => props.theme.colour.brand.secondary} 50%,
    ${(props) => props.theme.colour.brand.accent} 100%
  );
  color: white;
  position: relative;
  overflow: hidden;

  /* Efecto de textura natural */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
        circle at 20% 80%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(255, 255, 255, 0.05) 0%,
        transparent 50%
      ),
      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  /* Partículas flotantes que evocan naturaleza */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
        2px 2px at 20px 30px,
        rgba(255, 255, 255, 0.3),
        transparent
      ),
      radial-gradient(
        2px 2px at 40px 70px,
        rgba(255, 255, 255, 0.2),
        transparent
      ),
      radial-gradient(
        1px 1px at 90px 40px,
        rgba(255, 255, 255, 0.4),
        transparent
      ),
      radial-gradient(
        1px 1px at 130px 80px,
        rgba(255, 255, 255, 0.2),
        transparent
      ),
      radial-gradient(
        2px 2px at 160px 30px,
        rgba(255, 255, 255, 0.3),
        transparent
      );
    background-repeat: repeat;
    background-size: 200px 100px;
    animation: float-particles 20s linear infinite;
    pointer-events: none;
  }

  .landing-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(15px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    padding: 1rem 2rem;

    @media (max-width: 768px) {
      padding: 0.75rem 1rem;
    }

    &__content {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__logo-text {
      font-size: 1.5rem;
      font-weight: 700;
      color: white;
    }

    &__sublogo {
      font-size: 0.875rem;
      opacity: 0.9;
      color: white;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 1.5rem;

      @media (max-width: 768px) {
        gap: 1rem;
      }
    }

    &__link {
      color: white;
      text-decoration: none;
      font-weight: 500;
      transition: all 0.3s ease;
      padding: 0.5rem 1rem;
      border-radius: 25px;
      border: 1px solid transparent;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.3);
      }

      &--login {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.4);

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  .hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8rem 2rem 4rem;
    max-width: 1200px;
    margin: 0 auto;
    min-height: 80vh;

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      padding: 6rem 1rem 2rem;
    }

    &__content {
      flex: 1;
      max-width: 600px;
    }

    &__title {
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      line-height: 1.2;

      @media (max-width: 768px) {
        font-size: 2.5rem;
      }
    }

    &__subtitle {
      font-size: 1.25rem;
      margin-bottom: 2rem;
      opacity: 0.9;
      line-height: 1.6;
    }

    &__actions {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;

      @media (max-width: 768px) {
        justify-content: center;
      }
    }

    &__button {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 2rem;
      border-radius: 50px;
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;
      border: 2px solid transparent;

      &--primary {
        background: white;
        color: ${(props) => props.theme.colour.brand.primary};

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          background: ${(props) => props.theme.colour.background.primary};
        }
      }

      &--secondary {
        background: transparent;
        color: white;
        border-color: white;

        &:hover {
          background: white;
          color: ${(props) => props.theme.colour.brand.primary};
          transform: translateY(-2px);
        }
      }
    }

    &__visual {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__icons {
      display: flex;
      gap: 2rem;
      font-size: 4rem;
      opacity: 0.8;

      @media (max-width: 768px) {
        font-size: 3rem;
        gap: 1rem;
        margin-top: 2rem;
      }
    }

    &__icon {
      animation: float 3s ease-in-out infinite;

      &--mountain {
        animation-delay: 0s;
      }

      &--water {
        animation-delay: 1s;
      }

      &--home {
        animation-delay: 2s;
      }
    }
  }

  .features {
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &__title {
      text-align: center;
      font-size: 2.5rem;
      margin-bottom: 3rem;
      font-weight: 600;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
      max-width: 1200px;
      margin: 0 auto;
    }
  }

  .feature {
    text-align: center;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 15px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 0%,
        rgba(255, 255, 255, 0.05) 50%,
        transparent 100%
      );
      transform: translateX(-100%);
      transition: transform 0.6s ease;
    }

    &:hover {
      transform: translateY(-5px);
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

      &::before {
        transform: translateX(100%);
      }
    }

    &__icon {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      color: ${(props) => props.theme.colour.brand.light};
    }

    &__title {
      font-size: 1.25rem;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    &__description {
      opacity: 0.9;
      line-height: 1.6;
    }
  }

  .categories {
    padding: 4rem 2rem;
    max-width: 1200px;
    margin: 0 auto;

    &__title {
      text-align: center;
      font-size: 2.5rem;
      margin-bottom: 3rem;
      font-weight: 600;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 2rem;
    }
  }

  .category {
    text-align: center;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 15px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 0%,
        rgba(255, 255, 255, 0.05) 50%,
        transparent 100%
      );
      transform: translateX(-100%);
      transition: transform 0.6s ease;
    }

    &:hover {
      transform: translateY(-5px);
      border-color: rgba(255, 255, 255, 0.3);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

      &::before {
        transform: translateX(100%);
      }
    }

    &__icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: ${(props) => props.theme.colour.brand.light};
    }

    &__title {
      font-size: 1.5rem;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    &__description {
      opacity: 0.9;
      line-height: 1.6;
    }
  }

  .cta {
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(10px);
    text-align: center;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    &__content {
      max-width: 600px;
      margin: 0 auto;
    }

    &__title {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    &__description {
      font-size: 1.25rem;
      margin-bottom: 2rem;
      opacity: 0.9;
      line-height: 1.6;
    }

    &__button {
      display: inline-block;
      padding: 1rem 2rem;
      background: white;
      color: ${(props) => props.theme.colour.brand.primary};
      text-decoration: none;
      border-radius: 50px;
      font-weight: 600;
      transition: all 0.3s ease;
      border: 2px solid transparent;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        background: ${(props) => props.theme.colour.background.primary};
        border-color: ${(props) => props.theme.colour.brand.primary};
      }
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes float-particles {
    0% {
      transform: translateY(0px);
    }
    100% {
      transform: translateY(-100px);
    }
  }
`;

export default LandingPageStyled;
